import { later } from '@ember/runloop';
import EmberService, { service } from '@ember/service';

import type RESTAdapter from '@ember-data/adapter/rest';
import { SnapshotRecordArray } from '@ember-data/legacy-compat/-private';
import type { AdapterRequestError } from '@warp-drive/legacy/adapter/error';
import { Errors as DSErrors } from '@warp-drive/legacy/model/-private';
import $ from 'jquery';
import { Promise as RSVPPromise } from 'rsvp';

import type { APIData, EventData, RequestError, Service } from 'webapp-types';
import type { ModelName } from 'webapp/enums/enums';
import { ProductIssueType, RequestException } from 'webapp/enums/enums';
import { reload } from 'webapp/utils/common';
import { REQUEST_ID_XHR_FIELD } from 'webapp/utils/sentry';

import { asyncReplyEventName } from 'webapp/services/event-manager';
import type { EventIssueData } from 'webapp/services/issues-report';
import { Severity } from 'webapp/services/logger';
import type { LogoutLogData } from 'webapp/services/navigation';

function concatForKey<PART1 extends string, PART2 extends string>(prefix: PART1, end: PART2) {
	return ('_' + prefix + end) as `_${PART1}${PART2}`;
}

export interface FieldError {
	code?: number;
	field: string;
	message: string;
}

// an Ember Data request error, as adapters/application decorates it in handleResponse
export interface EDRequestError extends AdapterRequestError {
	originalErrors?: FieldError[];
	originalRequest?: { url: string };
	originalResponse?: { status?: number | string; payload?: APIData.ErrorReply }; // status: see RequestError.code
}

interface RequestFailError {
	url: string;
	response: string;
	data?: string;
	requestId?: string;
}

// Handlers attached to the request itself get its RequestError (a chained promise can also reject with what a handler
// threw).
export interface RSPromise<T> extends Promise<T> {
	jqPromise?: JQuery.jqXHR<unknown> | JQuery.Promise<unknown> | Promise<unknown>;
	then<TResult1 = T, TResult2 = never>(
		onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
		onrejected?: ((reason: RequestError) => TResult2 | PromiseLike<TResult2>) | null,
	): Promise<TResult1 | TResult2>;
	catch<TResult = never>(
		onrejected?: ((reason: RequestError) => TResult | PromiseLike<TResult>) | null,
	): Promise<T | TResult>;
}

export interface RSjqXHR extends JQuery.jqXHR {
	originalRequest: {
		url: string;
		data?: string; // data is already stringified
		requestId?: string;
	};
}

export type AsyncRequestBgEventHandlerFn = (payload: EventData.AsyncReply) => boolean; // returns true when we are "done"

// $.ajax settings plus our own (read, and mostly removed, by api() / app())
export interface RequestSettings extends JQuery.AjaxSettings {
	withoutPrefix?: boolean; // no /d prefix
	apiUrl?: string | null; // another base URL; also drops the X-Request-Id header
	publicUrl?: boolean; // no auth headers
	addSpaceId?: boolean; // X-Space-Id header
	requestId?: string; // X-Request-Id header (generated when missing)
	maxTryCount?: number; // retry on timeout
	skipShowingErrorInfolayer?: boolean;
}

// request body (JSON), or the query params of a GET
export type RequestData = object;

// dataRequestOnce keeps its flags on the caller, under "<prefix>Requested" / "<prefix>RequestPromise" keys
type DataRequestOnceState = Record<string, unknown> & {
	storeService?: Service.Store;
};

export default class RequestService extends EmberService {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('issues-report') declare issuesReportService: Service.IssuesReport;
	@service('logger') declare loggerService: Service.Logger;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('store') declare storeService: Service.Store;
	@service('account') declare accountService: Service.Account;

	// set when loading data
	apiUrl: string | null = null;
	connectApiUrl: string | null = null;
	authServiceUrl: string | null = null;
	amaBackendUrl = '';
	debugParam: string | null = null;
	wallToken: string | null = null; // set only on public datawall
	pusherEnvId: string | null = null;
	pusherKey: string | null = null;
	pusherCluster: string | null = null;
	datasetsBackendUrl = '';
	datasetsPusherKey = '';
	datasetsPusherCluster = '';
	datasetsSchemaExplorerUrl = '';
	aiBffUrl = '';
	authApiUrl = '';
	authGoogleClientId = '';
	websiteUrl = '';
	metricMoverCustomerUrl = '';
	metricMoverEnabled = false;
	metricMoverEnabledSpaceIds: number[] = [];

	isForceRelogging = false;

	handleTeapot(): void {
		this.navigationService.openReloadModal();
	}

	handleExpiredToken(forceLogout = false, data?: LogoutLogData): void {
		this.isForceRelogging = true;
		this.infolayerService.closeAll();

		const isDatawall = this.navigationService.isDatawallApp;

		if (!isDatawall && !this.navigationService.isMobileApp) {
			this.infolayerService.error('Authorization expired, redirecting...');
		}

		if (forceLogout) {
			// only when token refresh has failed
			this.navigationService.logout('handleExpiredToken - forceLogout', data);
		} else {
			// avoid reload loop on datawall
			if (!isDatawall || this.currentUserService.accessToken) {
				reload();
			}
		}
	}

	isReload(status: number): boolean {
		return status === 418; // I'm a teapot
	}

	isTooManyRequests(status: RequestError['code']): boolean {
		return status === 429; // with Retry-After header
	}

	isExpiredToken(status: number, errorsArray: FieldError[]): boolean {
		const possibleMessages = [
			'Invalid access_token provided.',
			'Invalid user token provided.',
			'Invalid user data token provided.',
			'Invalid token provided.',
			'Expired token provided',
		];
		return (
			status === 401 &&
			errorsArray?.length === 1 &&
			errorsArray[0]?.field === 'general' &&
			possibleMessages.some((msg) => String(errorsArray[0]?.message).startsWith(msg))
		);
	}

	isTimeout(_errorCode: RequestError['code'], errorText: string): boolean {
		// jXHR: Timeout
		// ED: The adapter operation timed out
		return (
			errorText.length > 0 &&
			(errorText.includes('timeout') || errorText.includes('timed out'))
		);
	}

	isAbort(errorCode: RequestError['code'], errorText: string): boolean {
		// jXHR: Abort
		// ED: The adapter operation was aborted
		return !errorCode && errorText.length > 0 && errorText.includes('abort');
	}

	getRequestIdFromPromise(promise: Promise<unknown> | RSPromise<unknown>) {
		return ((promise as RSPromise<unknown>).jqPromise as RSjqXHR)?.originalRequest?.requestId;
	}

	private getErrorPriority(errorCode: RequestError['code']): Severity | null {
		if (typeof errorCode === 'string') {
			// an Ember Data error's code matches none of the statuses below
			return errorCode ? Severity.INFO : Severity.WARNING;
		}

		let prio = null;

		if (errorCode >= 500) {
			prio = Severity.WARNING; // Severity.CRITICAL;
		} else if ([400, 404].includes(errorCode)) {
			prio = Severity.WARNING; // Severity.ERROR;
		} else if (!errorCode) {
			prio = Severity.WARNING; // Severity.ERROR;
		} else if (![-1, 403, 422].includes(errorCode)) {
			prio = Severity.INFO;
		}

		return prio;
	}

	private getUserErrorText(
		errorCode: RequestError['code'],
		url: string,
		errorText: string,
		errorsArray: FieldError[],
	): string | null {
		const ignoredUrls = [
			'/dataquery',
			'/scripts/preview',
			'/tracking',
			'/version',
			'/auth/refresh',
			'/feedbacks/report-issue',
			['/customqueries/', '/builder/', '/preview'], // multiple match patterns - customquery preview is handled with error in popup instead of infolayer
			['/customqueries/', '/builder/', '/extract-data'],
			'/boards/side-navigation',
			'/reports/side-navigation',
			'/user/side-navigation/favorites',
			'/teams/side-navigation',
			'/account/promotions',
			'/system-status',
			'/product-updates',
		];

		let text = null;

		const isIgnored = ignoredUrls.some((ignoredUrl) => {
			if (typeof ignoredUrl === 'string') {
				return url.includes(ignoredUrl);
			} else {
				return !ignoredUrl.some((ignoredUrlPart) => !url.includes(ignoredUrlPart));
			}
		});

		if (!isIgnored) {
			if (this.isTimeout(errorCode, errorText)) {
				text = 'We are experiencing some temporary issues. Please try again in a minute.';
			} else if (this.isTooManyRequests(errorCode)) {
				text =
					'We are experiencing high volume of requests. Please try again in a minute. If the problem persists, please contact customer support.';
			} else if (typeof errorCode !== 'number' || ![0, 422].includes(errorCode)) {
				const generalError = errorsArray.find((item) => item.field === 'general');

				if (generalError) {
					text = generalError.message;
				} else if (typeof errorCode === 'number' && [403, 404].includes(errorCode)) {
					text =
						'Requested item not found. If the problem persists, please contact customer support.';
				} else {
					text =
						'We have encountered an error. If the error persists, please contact customer support.';
				}
			}
		}

		return text;
	}

	handleGenericThrownError(errorData?: unknown): boolean | void {
		// not all errors thrown in requests are http request errors (a RequestError)
		// if f.ex success callback throws an error, the catch at the end of the promise gets it
		// here, we log and handle such errors
		let errorText;

		if (!errorData) {
			return;
		}

		if (typeof (errorData as Partial<RequestError>).code === 'undefined') {
			// TODO: enable this when we are confident this really an exception
			// this.infolayerService.error('We have ecountered an error. Your data might not be saved.');
			try {
				// eslint-disable-next-line @typescript-eslint/no-base-to-string -- whatever was thrown: an Error, a string, …
				errorText = String(errorData);
			} catch (_e) {
				errorText = 'Undefined'; // TODO: follow these and eliminate
			}
			this.loggerService.logError('(thrown in request) ' + errorText, errorData);

			return true;
		}
	}

	showGenericValidationError(errorData: RequestError): void {
		if (!errorData?.isHandled && !this.handleGenericThrownError(errorData)) {
			// shows general error for 422 - only when no fields defined
			// used whenever we show errors inline on forms as a fallback
			this.showValidationError(errorData, true);
		}
	}

	showAllValidationErrors(errorData: RequestError): void {
		if (!errorData?.isHandled && !this.handleGenericThrownError(errorData)) {
			// shows all validation errors for 422
			// used whenever we don't show errors inline in forms
			this.showValidationError(errorData);
		}
	}

	private showValidationError(errorData: RequestError, onlyGenericWhenNoFields?: boolean): void {
		const errors: string[] = [];

		const errorsWithCode = errorData?.errors?.filter((item) => item.code) || [],
			errorsWithoutCode = errorData?.errors?.filter((item) => !item.code) || [];

		errorsWithCode.forEach((error: FieldError) => {
			const errorText = this.getCodedErrorText(error);

			if (errorText) {
				errors.push(errorText);
			}
		});

		if (errorData?.code === 422 && errorsWithoutCode.length) {
			const genericError = errorsWithoutCode.find((item) => item.field === 'general'),
				fieldErrors = errorsWithoutCode.filter((item) => item.field !== 'general');

			if (genericError && !(onlyGenericWhenNoFields && fieldErrors.length)) {
				errors.push(genericError.message);
			}

			if (!onlyGenericWhenNoFields) {
				fieldErrors.forEach((error: FieldError) => {
					errors.push(error.field + ': ' + error.message);
				});
			}
		}

		if (errors.length) {
			this.infolayerService.error(errors.join('<br />'));
		}
	}

	private getCodedErrorText(error: FieldError): string | null {
		let text = null;

		switch (error.code) {
			case RequestException.USER_ALREADY_EXISTS:
				text =
					'User with this email already exists! It is possible that the user might have another Databox account. Learn more about how to add such a user to Databox <a href="https://help.databox.com/how-to-add-a-user-that-already-exists-in-the-databox-system" target="_blank">here</a>.';
				break;
			default:
				text = error.message;
		}

		return text;
	}

	parseErrors(payload: APIData.ErrorReply | null | undefined): FieldError[] {
		const errors: FieldError[] = [];

		if (!payload) {
			return [];
		}

		if (payload.error) {
			// BE API style
			// format https://databox.slack.com/archives/C03B1UKFXPX/p1666006436150119?thread_ts=1665564363.669819&cid=C03B1UKFXPX
			if (payload.error?.fields) {
				const { error } = payload;
				if (typeof error.fields === 'object') {
					// the new way
					Object.entries(error.fields).forEach(([field, fieldData]) => {
						errors.push({
							code: error.errorCode,
							field,
							message:
								typeof fieldData === 'string'
									? fieldData
									: (fieldData.message || error.message)!,
						});
					});
				} else if (typeof payload.error.fields === 'string') {
					// deprecated - when fields is a string (/d/users endpoints)
					const { errorCode, fields, message } = payload.error;

					errors.push({
						code: errorCode,
						field: fields,
						message: message!,
					});

					this.loggerService.logError(
						'(error.fields string deprecation)',
						{ payload: JSON.stringify(payload) },
						Severity.INFO,
					);
				}
			}

			if (errors.length === 0) {
				if (Object.keys(payload.error).length) {
					// general error (no fields)
					// in case error has more fields, return them all
					const errorData = {
						...payload.error,
						field: 'general',
						message: payload.error.message || 'Generic error',
					};
					errors.push(errorData);
				}
			}
		}

		if (payload.errors) {
			// AUTH SERVICE STYLE
			// format https://databox.slack.com/archives/C03B1UKFXPX/p1667815493595609?thread_ts=1667814830.119389&cid=C03B1UKFXPX
			// no code: these errors carry no domain error code (BE's errorCode), only the HTTP status
			Object.entries(payload.errors).forEach(([field, fieldData]) => {
				errors.push({
					field,
					message: fieldData.join(' '),
				});
			});

			if (errors.length === 0) {
				// general error (no fields)
				// in case error has more fields, return them all
				const errorData = {
					field: 'general',
					message: payload.title || 'Generic error',
				};
				errors.push(errorData);
			}
		}

		return errors;
	}

	private logError(
		errorCode: RequestError['code'],
		errorText: string,
		errorData: RequestFailError,
	): void {
		let adjustedErrorCode = errorCode,
			text = errorText;

		if (this.isTimeout(errorCode, errorText)) {
			text = 'Timeout';
			if (
				this.loggerService.spaceId === 335489 &&
				errorData.url.includes('/metrics/limitations')
			) {
				adjustedErrorCode = -1;
			} else {
				adjustedErrorCode = 0;
			}
		} else if (this.isAbort(errorCode, errorText)) {
			text = 'Aborted';
			adjustedErrorCode = -1;
		} else if (errorCode) {
			// don't log invalid token refreshes
			// assuming user logged off in another window which is valid
			if (errorCode === 401 && errorData?.url === 'POST /auth/refresh') {
				adjustedErrorCode = -1; // -1 is not being logged
			}
			text = String(errorCode);
		} else if (!errorData.response) {
			// not timeout, no error code, no response data
			const skipForUrls = ['POST /auth/refresh', 'POST /version'];
			if (skipForUrls.includes(errorData?.url)) {
				return;
			}
			text = 'CORS fail (?)';
		}

		const errorLevel = this.getErrorPriority(adjustedErrorCode);
		if (errorLevel) {
			this.loggerService.logError(`(Request) ${text}`, errorData, errorLevel);
		}
	}

	// for ED requests - used from requestQueue and findAll calls
	// dsError is {} when BufferedModel saves a deleted model: rethrown below
	onEDRequestFail(
		dsError: EDRequestError | DSErrors | Record<string, never>,
		dataParam?: object,
	): Promise<never> {
		if (this.isForceRelogging) {
			// don't throw more errors, don't even resolve
			return new Promise<never>((_resolve) => {
				// we're relogging, so leave it pending
			});
		}

		const requestError = dsError instanceof DSErrors ? null : dsError,
			errorText = requestError?.message || '';

		let errorsArray: FieldError[] = [],
			errorCode: RequestError['code'] = 0,
			isHandled = false,
			endpoint: string | undefined,
			requestId: string | undefined,
			userError: string | null = null;

		if (requestError?.originalRequest) {
			const response = requestError.originalResponse!; // set together with originalRequest
			requestId = response?.payload?.error?.requestId; // we get requestId from BE error response payload since its hard to pass requestId to adapter endpoints

			const errorData: RequestFailError = $.extend({}, requestError.originalRequest, {
				data: JSON.stringify(dataParam || {}),
				response: JSON.stringify(response.payload),
				requestId,
			});

			errorCode = response.status || 0;
			errorsArray = requestError.originalErrors || [];

			endpoint = requestError.originalRequest.url;

			// log error
			this.logError(errorCode, errorText, errorData);

			// check if we show error to user
			userError = this.getUserErrorText(errorCode, endpoint, errorText, errorsArray);
		} else if (dsError instanceof DSErrors) {
			// DS.Error is returned from BufferedModel when saving object that already has errors - no request here, don't log
			errorCode = 422;
			errorsArray = []; // TODO: parse? do we need them? model is already invalid
		} else {
			// code inbetween can get broken too - throw it forward so it can be handled in handleGenericThrownError
			throw dsError;
		}

		const eventIssueData: EventIssueData = {
			type:
				errorCode === 422
					? ProductIssueType.GENERAL_WARNING
					: ProductIssueType.GENERAL_ERROR,
			endpoint,
			page: window.location.pathname,
			url: window.location.href,
			httpStatusCode: errorCode,
			errorMessage: errorsArray[0]?.message || errorText,
			requestId,
		};

		this.issuesReportService.triggerEventIssue(eventIssueData);

		if (userError) {
			this.issuesReportService.showInfolayerError(userError, {
				endpoint: endpoint!, // userError is only set for requests
				errorText,
				httpStatusCode: errorCode,
				requestId,
				eventIssueData,
			});
			isHandled = true;
		}

		return Promise.reject({
			code: errorCode,
			errors: errorsArray,
			isHandled,
			obj: dsError,
		});
	}

	// resolves with the bgEvent reply; T adds the fields that the endpoint's reply carries on top of the records
	apiWithAsyncReply<T extends object = object>(
		method: string,
		url: string,
		data?: RequestData,
		extraSettings?: RequestSettings,
		bgEventHandler?: AsyncRequestBgEventHandlerFn,
	): Promise<EventData.AsyncReply & T> {
		return new Promise((resolve, reject) => {
			this.api<APIData.AsyncRequestResponse>(method, url, data, extraSettings).then(
				(reply) => {
					if (reply?.requestId) {
						// wait for async event
						const origin = reply.origin || '';
						this.eventManagerService.on(
							asyncReplyEventName(origin, reply.requestId),
							this,
							(eventData) => {
								if (eventData.success) {
									if (bgEventHandler) {
										const isDone = bgEventHandler(eventData);
										if (isDone) {
											resolve(eventData as EventData.AsyncReply & T);
										}
										return isDone;
									} else {
										resolve(eventData as EventData.AsyncReply & T);
										return true; // without handler just do it once
									}
								} else {
									reject({
										code: 422, // so the showAllValidationErrors grabs it
										errors: [
											{
												field: 'general',
												message: eventData.message || 'There was an error',
											},
										],
									});
									return true; // interrupt on error, no matter if we have bgEventHandler or not
								}
							},
						);
					} else {
						this.loggerService.logError(
							'No requestId',
							{
								method,
								url,
								data: this.loggerService.prepareJSONForLogging(data),
							},
							Severity.CRITICAL,
						);
						reject({
							code: 422, // so the showAllValidationErrors grabs it
							errors: [
								{
									field: 'general',
									message: 'No requestId',
								},
							],
						});
					}
				},
				(errorData: RequestError) => {
					reject(errorData);
				},
			);
		});
	}

	storeFindAll(modelName: ModelName): Promise<void> {
		const store = this.storeService;

		return store.findAll(modelName).then(
			(result: unknown) => {
				void Promise.resolve(result);
			},
			(dsError: EDRequestError) => {
				// the following two IFs might be unnecessary
				// bcos adapters/application:handleResponse happens before this
				if (!dsError.originalRequest) {
					dsError.originalRequest = {
						url: (store.adapterFor(modelName) as RESTAdapter).buildURL(
							modelName,
							null,
							new SnapshotRecordArray(this.storeService, modelName),
							'findAll',
						),
					};
				}
				if (!dsError.originalResponse) {
					dsError.originalResponse = {
						// not an HTTP status but the error's code ('AdapterError', …): a non-numeric code still gets the
						// generic error shown to the user (getUserErrorText), where 0 would fail silently
						status: dsError.code,
					};
				}
				return this.onEDRequestFail(dsError);
			},
		);
	}

	retryWrapper(ajaxSettings: JQuery.AjaxSettings, maxTryCount: number): JQuery.Promise<unknown> {
		const deferred = $.Deferred();
		let retryCount = 0;

		const callFn = (settings: JQuery.AjaxSettings) => {
			retryCount++;

			$.ajax(settings)
				.done((results) => {
					deferred.resolve(results);
				})
				.fail((jqXHR, status, error) => {
					if (status === 'timeout' && retryCount <= maxTryCount) {
						const newSettings = Object.assign({}, settings),
							errorData = $.extend({}, (jqXHR as RSjqXHR).originalRequest, {
								response: jqXHR.responseText,
							});
						if (retryCount > 2) {
							// don't log first or second timeout
							this.loggerService.logError(
								`Timeout on try ${retryCount}`,
								errorData,
								retryCount === maxTryCount ? Severity.CRITICAL : Severity.INFO,
							);
						}
						newSettings.timeout = (settings.timeout || 30000) + 15000;
						callFn(newSettings);
					} else {
						deferred.reject(jqXHR, status, error);
					}
				});
		};

		callFn(ajaxSettings);

		return deferred.promise();
	}

	generateRequestId(): string {
		// 13 char long alphanumeric string
		return (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(
			0,
			13,
		);
	}

	// T is the reply; void when it isn't read
	app<T = void>(
		type: string,
		url: string,
		data?: RequestData | null,
		extraSettings?: RequestSettings,
	): RSPromise<T> {
		if (this.currentUserService.isDemo) {
			const allowedPOSTUrlsOnDemo = [
					'/d/tracking',
					'/d/summaries',
					'/d/forecasts',
					'/d/dataquery',
					'/d/blocks/sample',
					'/d/metric-insights/forecast/data',
					'/d/metric-insights',
					'/d/metrics/dimensions/values',
				],
				isRestrictedPOSTUrl =
					type === 'POST' &&
					!allowedPOSTUrlsOnDemo.some((allowedUrl) => url.includes(allowedUrl));

			if (['PUT', 'DELETE'].includes(type) || isRestrictedPOSTUrl) {
				// handled, so the generic handlers show nothing
				const blockedOnDemo: RequestError = {
					code: 0,
					errors: [],
					isHandled: true,
					isBlockedOnDemo: true,
				};
				return Promise.reject(blockedOnDemo);
			}
		}

		let paramPath, formattedData: string | null;

		if (!extraSettings) {
			extraSettings = {};
		}

		if (window.isSnapshooterActive) {
			this.loggerService.logError(
				'Requests on active snapshot',
				{ url, type, token: this.wallToken },
				Severity.CRITICAL,
			);
		}

		const maxTryCount = extraSettings.maxTryCount;

		delete extraSettings.maxTryCount;

		if (data) {
			if (type === 'GET') {
				const params = data as Record<string, unknown>;
				paramPath = Object.keys(params)
					.map((key) => {
						let value = params[key];
						if (Array.isArray(value)) {
							value = value.join(',');
						}
						return `${key}=${String(value)}`;
					})
					.join('&');
				if (paramPath) {
					url += (url.includes('?') ? '&' : '?') + paramPath;
				}
				formattedData = null;
			} else {
				formattedData = JSON.stringify(data);
			}
		} else {
			formattedData = null;
		}

		const defaultSettings = {
			type,
			url,
			data: formattedData,
			contentType: 'application/json; charset=utf-8',
			processData: false,
			beforeSend: (jqXHR: RSjqXHR, settings: JQuery.AjaxSettings) => {
				jqXHR.originalRequest = {
					url: settings.type + ' ' + url,
					data: this.loggerService.prepareJSONForLogging(settings.data),
					requestId: (settings.headers?.['X-Request-Id'] as string | undefined) || '',
				};
			},
		};

		const settings: JQuery.AjaxSettings = $.extend(defaultSettings, extraSettings);

		// calls to our own server get an id too (api() already set one for API calls); other hosts may not
		// allow the header
		if (
			!settings.headers?.['X-Request-Id'] &&
			new URL(url, window.location.href).origin === window.location.origin
		) {
			settings.headers = { ...settings.headers, 'X-Request-Id': this.generateRequestId() };
		}

		const requestId = settings.headers?.['X-Request-Id'];
		if (typeof requestId === 'string') {
			// jQuery copies xhrFields onto the native XHR, where the Sentry breadcrumb reads it
			// (jQuery's XHRFields type only lists a few standard properties)
			settings.xhrFields = {
				...settings.xhrFields,
				[REQUEST_ID_XHR_FIELD]: requestId,
			} as JQuery.AjaxSettings['xhrFields'];
		}

		let jqPromise: JQuery.Promise<unknown> | RSjqXHR;
		if (maxTryCount) {
			jqPromise = this.retryWrapper(settings, maxTryCount);
		} else {
			jqPromise = $.ajax(settings) as RSjqXHR;
		}

		const deferred = $.Deferred();

		(jqPromise as JQuery.Promise<unknown>).then(
			(reply) => {
				return deferred.resolve(reply);
			},
			(jqXHR: RSjqXHR, _status: number, error: string) => {
				if (this.isForceRelogging) {
					// don't throw more errors, don't even resolve
					return new Promise((resolve) => {
						later(resolve, null, 5000);
					});
				}

				const errorCode = +jqXHR.status || 0,
					errorText = error || jqXHR.statusText,
					errorsArray = this.parseErrors(
						jqXHR.responseJSON as APIData.ErrorReply | undefined,
					);

				let isHandled = false,
					errorData: RequestFailError,
					userError: string | null = null;

				if (this.isReload(jqXHR.status)) {
					this.handleTeapot();
					isHandled = true;
				} else if (this.isExpiredToken(jqXHR.status, errorsArray)) {
					this.handleExpiredToken();
					isHandled = true;
				} else {
					errorData = $.extend({}, jqXHR.originalRequest, {
						response: jqXHR.responseText,
					});

					// log error
					this.logError(errorCode, errorText, errorData);

					if (extraSettings && !extraSettings.skipShowingErrorInfolayer) {
						// check if we show error infolayer to user
						userError = this.getUserErrorText(
							errorCode,
							errorData.url,
							errorText,
							errorsArray,
						);
					}
				}

				const ignoreErrorsForURLs = ['/d/tracking'],
					isURLIgnored = ignoreErrorsForURLs.some((ignoredUrl) =>
						url.includes(ignoredUrl),
					);

				const eventIssueData: EventIssueData = {
					type:
						errorCode === 422
							? ProductIssueType.GENERAL_WARNING
							: ProductIssueType.GENERAL_ERROR,
					endpoint: jqXHR.originalRequest.url,
					page: window.location.pathname,
					url: window.location.href,
					httpStatusCode: errorCode,
					errorMessage: errorsArray[0]?.message || errorText,
					duration: settings.timeout,
					requestId: jqXHR.originalRequest.requestId!,
				};

				if (!isURLIgnored) {
					this.issuesReportService.triggerEventIssue(eventIssueData);
				}

				if (userError) {
					this.issuesReportService.showInfolayerError(userError, {
						endpoint: jqXHR.originalRequest.url,
						httpStatusCode: errorCode,
						errorText,
						requestId: jqXHR.originalRequest.requestId!,
						eventIssueData,
					});
					isHandled = true;
				}

				return deferred.reject({
					code: errorCode,
					errors: errorsArray,
					isHandled,
					obj: jqXHR,
				});
			},
		);

		// wrap jquery promise in ember promise
		const emberPromise: RSPromise<T> = new Promise((resolve, reject) => {
			deferred.promise().then(resolve, reject);
		});

		emberPromise.jqPromise = jqPromise; // adding reference to jquery promise so we can use abort() on it

		return emberPromise;
	}

	// T is the reply; void when it isn't read
	api<T = void>(
		type: string,
		url: string,
		data?: RequestData | null,
		extraSettings?: RequestSettings,
	): RSPromise<T> {
		let apiUrl = this.apiUrl,
			skipTokens = false,
			addRequestId = true,
			pathUrl = '/d',
			spaceId = null,
			requestId: string | null = null;

		if (extraSettings) {
			if (extraSettings.withoutPrefix) {
				pathUrl = '';
				delete extraSettings.withoutPrefix;
			}
			if (extraSettings.apiUrl) {
				apiUrl = extraSettings.apiUrl;
				delete extraSettings.apiUrl;
				addRequestId = false;
			}
			if (extraSettings.publicUrl) {
				skipTokens = true;
			}
			if (extraSettings.addSpaceId) {
				spaceId = this.accountService.id;
				delete extraSettings.addSpaceId;
			}
			if (extraSettings.requestId) {
				requestId = extraSettings.requestId;
				delete extraSettings.requestId;
			}
		}
		pathUrl += url;

		pathUrl += this.debugParam || '';

		const defaultSettings: { headers: Record<string, string | number> } = {
			headers: {},
		};

		if (!skipTokens) {
			const authHeader = this.currentUserService.authTokenAuthorizationHeader;
			if (authHeader) {
				defaultSettings.headers.Authorization = authHeader;
			}
			const authHeaderX = this.currentUserService.userTokenAuthorizationHeader;
			if (authHeaderX) {
				defaultSettings.headers['X-Authorization'] = authHeaderX;
			}
		}

		if (spaceId) {
			defaultSettings.headers['X-Space-Id'] = spaceId;
		}

		if (addRequestId) {
			if (!requestId) {
				requestId = this.generateRequestId();
			}

			defaultSettings.headers['X-Request-Id'] = requestId;
		}

		const settings = $.extend(true, defaultSettings, extraSettings);

		return this.app(type, apiUrl + pathUrl, data, settings);
	}

	authService<T = void>(
		type: string,
		url: string,
		data?: RequestData | null,
		extraSettings?: RequestSettings,
	): RSPromise<T> {
		if (!extraSettings) {
			extraSettings = {};
		}
		extraSettings.withoutPrefix = true;
		extraSettings.apiUrl = this.authServiceUrl;

		return this.api<T>(type, url, data, extraSettings);
	}

	connectapi<T = void>(
		type: string,
		url: string,
		data?: RequestData | null,
		extraSettings?: RequestSettings,
	): RSPromise<T> {
		if (!extraSettings) {
			extraSettings = {};
		}
		extraSettings.withoutPrefix = true;
		extraSettings.apiUrl = this.connectApiUrl;

		return this.api<T>(type, url, data, extraSettings);
	}

	aiBff<T = void>(
		type: string,
		url: string,
		data?: RequestData | null,
		extraSettings?: RequestSettings,
	): RSPromise<T> {
		if (!extraSettings) {
			extraSettings = {};
		}
		extraSettings.withoutPrefix = true;
		extraSettings.apiUrl = this.aiBffUrl;
		extraSettings.addSpaceId = true;
		extraSettings.headers = {
			'X-User-Id': String(this.currentUserService.id),
			...(extraSettings.headers ?? {}),
		};

		return this.api<T>(type, url, data, extraSettings);
	}

	dataRequestOnce<VarPrefixType extends string>(
		caller: object,
		variablePrefix: VarPrefixType,
		requestPromiseFn: () => Promise<object | void>,
		preStoreFn?: () => Promise<void>,
		storeFn?: (payload: object) => void,
		throwOnFail = false,
	): Promise<void> {
		const context = caller as DataRequestOnceState,
			requestedVariableName = concatForKey(variablePrefix, 'Requested'),
			requestPromiseVariableName = concatForKey(variablePrefix, 'RequestPromise');

		if (context[requestedVariableName]) {
			// NOT the same as native Promise if used in beforeModel hooks
			// native can cause the *-loading template to appear
			// ref: https://recordit.co/KY0G8IY9Bq
			return RSVPPromise.resolve();
		} else {
			const requestPromise = context[requestPromiseVariableName] as
				Promise<void> | null | undefined;
			if (requestPromise) {
				return requestPromise;
			} else {
				const promise = requestPromiseFn()
					.then((payload) => {
						const preStorePromise = preStoreFn ? preStoreFn() : Promise.resolve();
						void preStorePromise.then(() => {
							if (payload) {
								if (storeFn) {
									storeFn(payload);
								} else if (context.storeService) {
									context.storeService.pushPayload(payload);
								}
							}
							context[requestedVariableName] = true;
							context[requestPromiseVariableName] = null;
						});
					})
					.catch((errorData: RequestError) => {
						if (throwOnFail) {
							throw errorData;
						} else {
							this.handleGenericThrownError(errorData);
						}
					});
				context[requestPromiseVariableName] = promise;
				return promise;
			}
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		request: RequestService;
	}
}
